// Split out of src/components/Tests.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Tests.jsx
// re-exports everything it exported before, so existing imports keep working.

import { DoubledTokens, Celebration, Icon } from "../Common.jsx";
import { useGameTelemetry } from "../../lib/metrics.js";
import { oinkSessionOP } from "../../lib/oinks.js";
import { DEFAULT_PASS_THRESHOLD, shuffle } from "../../lib/constants.js";
import { audioKeyFor, isSpeechAvailable, prefetchClip, primeVoices, resetRetryBudget, resumeAfterForeground, speak as speakText, stopSpeaking, unlockAudio } from "../../lib/speech.js";
import { t as ct } from "../../lib/content.js";
import { deferToEnd, playDing, playError, OinkIntroLine, TestOverlay, scoreEmoji, TokenRewardRow, OinkRewardRow, ProgressDots } from "./shared.jsx";
import { LetterKeyboard } from "./keyboards.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// LB-366: a spelling answer as the test history shows it — the word, what was typed, and
// whether it was skipped (a skip scores as a miss and has nothing typed).
function spellingHistoryRows(answers) {
  return (answers || []).map((a) => ({ q: a.word, given: a.given || "", answer: a.word, correct: !!a.correct, skipped: !!a.skipped }));
}

export function SpellingTest({ words, hasAnyWords, doubleDay = false, onLearnEarn, reward, passReward = 0, passThreshold = DEFAULT_PASS_THRESHOLD, payoutsPerWeek, payGate, payoutsUsed, kidTheme, weeklyCapMessage, oinksOn, onEarnOinks, partyEmoji, onSessionFinished, onItemResults }) {
  const firstTryRef = useRef({}); // word → correct on FIRST attempt (a retry-corrected word was still a miss for Adaptive)
  const autoplayedRef = useRef(null); // "sessionKey:round:idx" of the word already autoplayed — see the autoplay effect
  // The first round's answers in full, kept because `answers` is reset when the practice
  // round starts. The score and the "words to learn" list are both drawn from here now, so
  // what the results screen shows and what it pays are the same attempt (Alan, 13 Sep).
  const firstAnswersRef = useRef([]);
  const answersRef = useRef([]); // this round's answers, never a render behind — see check()
  const [sessionKey, setSessionKey] = useState(0);
  const [idx, setIdx] = useState(0);
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [phase, setPhase] = useState("intro");
  // W2.5 Learn & Earn funnel: game_start / game_complete / game_abandon ride the
  // shared phase shape (intro → testing → finished). Age band is stamped by the
  // metrics module (App.jsx keeps it current), so no child prop is needed here.
  useGameTelemetry("spelling", phase);
  // Tier 3 of TTS_SCOPE.md §2: false only once a MANUAL listen attempt has exhausted its
  // retry and neither a generated clip nor the device voice could speak. lib/speech.js owns
  // both tiers above it; this component only cares whether anything at all worked.
  const [speechOk, setSpeechOk] = useState(true);
  // Held purely so the voice-priming effect re-runs the autoplay once a device voice finally
  // becomes available (browsers populate getVoices() asynchronously) — never read directly.
  const [voiceReady, setVoiceReady] = useState(false);
  // The pre-generated clip id for the current word, computed from its text (see the effect
  // below). null means "not worked out yet, or not possible here" — either way speak()
  // falls through to the device voice, which is the correct behaviour for a word that has
  // never been generated, for a parent's own custom word, and for a browser with no
  // WebCrypto. Nothing waits on this.
  // Words the child has parked once already — a second skip on one of these is final.
  const deferredRef = useRef(new Set());
  const [audioKey, setAudioKey] = useState(null);
  // Whether the audio tier for THIS word is settled (key resolved, and its clip confirmed
  // present or missing). Autoplay waits for it — see the effect below.
  const [audioReady, setAudioReady] = useState(false);
  const [awarded, setAwarded] = useState(false);
  // Set when onLearnEarn routes this pass's reward to the parent instead of crediting it
  // instantly (spending cap breached/blocked) — holds the kid-friendly message to show in
  // place of the usual "Full marks! +N tokens" celebration.
  const [capNotice, setCapNotice] = useState(null);
  // round 0 = the real test; round 1 = an immediate, automatic one-shot retry of just the
  // words gotten wrong in round 0, with no results screen shown in between. Getting every
  // retried word right on this single retry attempt still earns the full reward — it only
  // fails to pay out if a word is wrong again on the retry too.
  const [round, setRound] = useState(0);
  const [retryWords, setRetryWords] = useState(null);
  const [totalWords, setTotalWords] = useState(0);
  const advanceRef = useRef(null);
  // Correct answers in the FIRST attempt, for the session's Oinks — paid when the test
  // settles at the end of round 0. Practice-round words pay their own Oink one at a time in
  // check(), so this deliberately stops counting once the practice round starts. A ref (not
  // state): nothing re-renders off it mid-test.
  const opCorrectRef = useRef(0);
  // A test abandoned mid-first-attempt still reaches Coach, as a "stopped early" entry that
  // pays nothing and moves nothing (Alan, 22 Sep — Freddie's test left no trace at all).
  // One report per session; refs throughout, because the unmount cleanup below runs with a
  // closure captured at mount. Leaving during PRACTICE reports nothing: the test settled
  // before practice could start, so there is nothing unsaved to report.
  const stoppedReportedRef = useRef(false);
  const phaseRef = useRef(phase); phaseRef.current = phase;
  const roundRef = useRef(round); roundRef.current = round;
  const totalRef = useRef(totalWords); totalRef.current = totalWords;
  const onItemResultsRef = useRef(onItemResults); onItemResultsRef.current = onItemResults;
  const passThresholdRef = useRef(passThreshold); passThresholdRef.current = passReward > 0 ? passThreshold : 100;

  const perWeek = payoutsPerWeek || 1;
  // 12 Sep (Alan): on Coach and By-Year lists a game pays once at the PASS tier and once at
  // FULL marks, so scoring 80% no longer ends the week — going back for 100% still pays,
  // which is the behaviour worth encouraging. Custom lists keep the plain weekly count,
  // because replaying a parent's own short word list IS farmable. payGate falls back to the
  // old rule when a caller has not supplied one.
  const gate = payGate || { full: (payoutsUsed || 0) < perWeek, pass: (payoutsUsed || 0) < perWeek };
  const onCooldown = !gate.full && !gate.pass;
  // Snapshot of onCooldown taken the moment this test finishes, then frozen. Without this,
  // the results screen has a real bug: crediting THIS session's reward updates payoutsUsed
  // via the parent's state, which re-renders this component with onCooldown now true —
  // since the very payout being reported is what just pushed the weekly count to its limit
  // — so the "you earned it" card flips to "you're already on cooldown" the instant the
  // reward that just earned it lands (staging feedback, Aug 2026). Freezing the value the
  // moment phase becomes "finished" keeps the results screen honest about the session it's
  // actually reporting on, rather than about state that session's own payout just changed.
  const onCooldownAtFinishRef = useRef(onCooldown);
  if (phase !== "finished") onCooldownAtFinishRef.current = onCooldown;

  // SAME BUG AS VOCAB, FIXED THE SAME WAY (2026-08-20). This used to be
  //   useMemo(() => shuffle(round === 1 ? retryWords : words), [words, sessionKey, round, retryWords])
  // which reshuffled the session onto a different set of words every time `words` changed
  // reference — and `words` is a derived prop (ChildView's spellingTestWords), so it gets a
  // new reference on any household state change and on any re-sync after the app is
  // backgrounded and reopened. `idx` and `answers` didn't reset when that happened, so the
  // test silently jumped to a different word, sometimes one already answered: the same word
  // could be asked twice, `answers` no longer matched what was actually shown, and the
  // final score was wrong. A child who worked out that closing and reopening reshuffled the
  // test could farm it.
  //
  // VocabTest hit this on 2026-08-15 and was fixed by drawing the session into plain state
  // exactly once; the fix was never carried across to here. Drawing it in startTest() and
  // in the retry transition means nothing outside those two points can change the session
  // mid-attempt — which is what "the session's words are already locked in" further down
  // has always claimed, and now actually is.
  const [session, setSession] = useState([]);
  const sessionRef = useRef(session); sessionRef.current = session;
  function reportStopped() {
    if (stoppedReportedRef.current) return;
    if (phaseRef.current !== "testing" || roundRef.current !== 0) return;
    const rows = answersRef.current;
    if (!rows.length) return; // walked away before answering anything — nothing happened yet
    stoppedReportedRef.current = true;
    const byWord = Object.fromEntries(sessionRef.current.map((w) => [w.word, w]));
    // LB-366: the history carries what the child actually typed, which the engine rows do not.
    const history = { questions: spellingHistoryRows(rows), oinks: 0, qualified: null, gateOpen: false, passMark: passThresholdRef.current, passKind: "pct" };
    if (onItemResultsRef.current) onItemResultsRef.current("spelling", rows.map((a) => { const w = byWord[a.word] || {}; return { id: w.id, word: a.word, grade: w.grade, topic: w.topic, correct: a.correct }; }), true, { total: totalRef.current, history });
  }
  // LB-290 — the progress row. A slot per PROMPT, in the order the child meets them, which is
  // not the same as a slot per word: a skipped word leaves a marker where it was AND gains a
  // fresh slot at the end, so one word can hold two. Slots are consumed strictly in order —
  // every answer and every skip consumes exactly one — which is what keeps this in step with
  // `session` without either knowing about the other.
  const [slots, setSlots] = useState([]);
  const slotIdxRef = useRef(0);
  const word = session[idx];
  // `answers` always holds only the current round's attempts (reset when the retry round
  // starts), so whichever words are still wrong in it are the true final-wrong list —
  // whether that round was the original test (never needed a retry) or the retry itself.
  const finalWrong = answers.filter((a) => !a.correct);
  // The first round's attempts, wherever the session ended: `answers` still holds them when
  // a clean sweep finished in round 0, and firstAnswersRef holds them once a practice round
  // has reset it.
  const firstAnswers = round === 0 ? answers : firstAnswersRef.current;
  const firstWrong = firstAnswers.filter((a) => !a.correct);
  // 15 Sep, found on staging: this read the LIVE `words` prop while firstWrong read the
  // recorded answers — two things scoring one event from different sources, inside one
  // function, which is the exact rule decision 56 exists to enforce. Under Coach the prop is
  // a fresh adaptiveDraw on every memo recompute, so any state save mid-test (a token award,
  // an Oink) reshuffled it and the score collapsed to 0 while the wrong-word list stayed
  // correct. Alan saw 0/15 having got 13/15. Custom lists were unaffected because their pool
  // does not reshuffle, which is what made it look like a Coach bug rather than a scoring one.
  //
  // Both now come from the SAME array: what the child actually answered, first time.
  const firstTryCorrect = firstAnswers.filter((a) => a.correct).length;
  const firstTryPct = totalWords > 0 ? (100 * firstTryCorrect) / totalWords : 0;

  useEffect(() => {
    if (!isSpeechAvailable()) { setSpeechOk(false); return; }
    return primeVoices(() => setVoiceReady(true));
  }, []);
  // iOS Safari (especially running as an installed app) commonly suspends the speech engine
  // whenever the app is backgrounded — switching apps, locking the screen, even a brief
  // app-switcher swipe — and the next speak() call can throw a spurious error even though
  // nothing is actually broken. Nudge it back to life when the app returns to the foreground,
  // and give audio another chance rather than leaving it disabled for the rest of the session.
  useEffect(() => {
    function handleVisible() {
      if (resumeAfterForeground()) setSpeechOk(true);
    }
    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("pageshow", handleVisible);
    return () => {
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("pageshow", handleVisible);
    };
  }, []);
  useEffect(() => {
    if (phase === "finished" && !awarded) {
      setAwarded(true);
      // Oinks first: paid on every completed session regardless of cooldown, cap, or score —
      // the token payout below keeps all of its own conditions untouched.
      const sessionOinks = oinksOn && onEarnOinks ? oinkSessionOP(opCorrectRef.current, finalWrong.length === 0 && totalWords > 0).op : 0;
      if (oinksOn && onEarnOinks) onEarnOinks(sessionOinks);
      if (onSessionFinished) onSessionFinished("spelling", opCorrectRef.current, totalWords, finalWrong.length === 0 && totalWords > 0);
      // LB-366: which tier this attempt reached and whether its payout was open, so the
      // history can say why a test did not pay. Mirrors the two branches below exactly.
      const fullQ = firstTryCorrect === totalWords && totalWords > 0;
      const passQ = passReward > 0 && totalWords > 0 && firstTryPct >= passThreshold;
      const history = {
        questions: spellingHistoryRows(firstAnswersRef.current.length ? firstAnswersRef.current : answersRef.current),
        oinks: sessionOinks,
        qualified: fullQ ? "full" : passQ ? "pass" : null,
        gateOpen: (fullQ && gate.full) || (passQ && gate.pass),
        passMark: passReward > 0 ? passThreshold : 100,
        passKind: "pct",
      };
      // Adaptive (LB-19): per-item results on the FIRST attempt — mapped over the SESSION the
      // child actually sat, never the live `words` prop. Under Coach that prop is a fresh
      // draw on every memo recompute, so a state save mid-test could hand this line a set of
      // words the child never saw and Coach recorded 0/N for a test the child aced (found
      // 22 Sep, proven in scripts/check-spelling-score.mjs).
      if (onItemResults) onItemResults("spelling", session.map((w) => ({ id: w.id, word: w.word, grade: w.grade, topic: w.topic, correct: firstTryRef.current[w.word] === true })), false, { total: totalWords, history });
      // Alan, 13 Sep: the FIRST attempt is the score, for both tiers and everywhere it is
      // shown. It used to pay full marks on `finalWrong.length === 0` — which is the state
      // AFTER the retry round — so a child who got 15 of 19 and then fixed the other four
      // when shown the answers earned exactly the same as a child who got all 19 cold, and
      // the activity line claimed "full marks (19/19)" for a thing that never happened.
      // Coach, which has always logged the first attempt, then disagreed with the payout on
      // the same test. One attempt, one number, and the retry round becomes practice.
      if (firstTryCorrect === totalWords && totalWords > 0 && gate.full) {
        const result = onLearnEarn(reward, `Spelling Test: full marks (${totalWords}/${totalWords})`, "spelling", "full");
        if (result && !result.credited) setCapNotice(result.capMessage || null);
      } else if (passReward > 0 && totalWords > 0 && firstTryPct >= passThreshold && gate.pass) {
        // LB-19 §7 — the pass tier, like vocab.
        const result = onLearnEarn(passReward, `Spelling Test: ${firstTryCorrect}/${totalWords} first time (${Math.round(firstTryPct)}%)`, "spelling", "pass");
        if (result && !result.credited) setCapNotice(result.capMessage || null);
      }
    }
  }, [phase]);
  // Autoplay the word 1 second after it loads, so kids can listen hands-free before typing.
  // Work out this word's clip id as soon as the word changes, so it's ready before the
  // autoplay fires a second later. Hashing is a few microseconds, but it's async (WebCrypto
  // returns a promise), so doing it here rather than inside speak() keeps the play path
  // synchronous and avoids a race where a fast tap beats the hash.
  //
  // Stale-result guard: if the word changes while a hash is in flight, the late result must
  // not overwrite the new word's key — otherwise a child who advances quickly would hear
  // the previous word.
  //
  // BUG FOUND AND FIXED (2026-08-20, real CMS testing): this used to hash `word.word`
  // unconditionally, but the server (worker/lib/tts-content.js narrationTextFor) hashes
  // `speakAs` INSTEAD of `word` whenever a word has one set (D13 — the homophone override,
  // e.g. "our" vs "hour"). Any word with speakAs filled in therefore had a permanently
  // mismatched client hash: the generated clip genuinely existed in R2 under the SERVER's
  // hash, the Audio tab correctly showed it as covered, but the CLIENT was requesting a
  // completely different hash that could never exist — a 404 forever, not a timing issue.
  // Mirroring the server's own text-selection rule here is what actually closes the gap;
  // duplicating narrationTextFor's logic client-side rather than sharing one function is
  // the same deliberate trade-off as the rest of this hash formula (see audioKeyFor's own
  // header comment in speech.js) — no build step joins the two files, so this is the price
  // of computing the hash without a round-trip.
  // LB-292: mirrors worker/lib/tts-content.js narrationTextFor EXACTLY, including the ". "
  // join. The two are duplicated deliberately (no build step joins them) and a mismatch is a
  // permanent 404 rather than a timing bug — which is precisely what happened when speakAs was
  // added to one side only. Change one, change both.
  // Slots still waiting at the end — the number the position line reports, and the only place
  // the free-first-skip rule is ever stated to a child.
  const comingBack = slots.filter((x) => x === "requeued").length;
  const speechBaseForWord = (word && word.speakAs && word.speakAs.trim()) || (word && word.word) || "";
  const wordDescription = (word && typeof word.description === "string" && word.description.trim()) || "";
  const speechTextForWord = speechBaseForWord && wordDescription ? `${speechBaseForWord}. ${wordDescription}` : speechBaseForWord;
  useEffect(() => {
    let live = true;
    setAudioKey(null);
    setAudioReady(false);
    if (!speechTextForWord) { setAudioReady(true); return () => { live = false; }; }
    audioKeyFor(speechTextForWord, "teacher").then(async (k) => {
      if (!live) return;
      setAudioKey(k);
      // Settle whether the clip EXISTS too, not just what it would be called — see
      // prefetchClip in speech.js for why the iOS fallback depended on knowing early.
      if (k) await prefetchClip(k);
      if (live) setAudioReady(true);
    });
    return () => { live = false; };
  }, [speechTextForWord]);

  useEffect(() => {
    resetRetryBudget(); // fresh retry budget for each new word
    if (phase !== "testing" || !speechOk || !word) return;
    // 12 Sep (Android, custom lists): the word was read twice — device voice first, then the
    // generated clip. The cause was this effect running once before the audio key resolved
    // and once after; when the key took longer than the 1s timer, both spoke. Nothing starts
    // until the key has settled, so exactly one tier speaks per word.
    if (!audioReady) return;
    // 13 Sep (Android PWA, custom lists again): a SECOND route to the same symptom, which the
    // audioReady guard above cannot close. `voiceReady` is a dependency, and primeVoices
    // retries for up to 3 seconds — Chrome on Android loads voices lazily, slower still on a
    // cold PWA start. When voices land AFTER the 1s timer has already fired, this effect
    // re-runs, its cleanup clears a timer that has already gone off, and the word is spoken
    // a second time. Custom words are where it is audible: with no generated clip they go to
    // speechSynthesis, which QUEUES rather than interrupts, so the word is simply read twice.
    // A word with a clip restarts the same audio and mostly hides it.
    //
    // Guarding on the dependencies themselves would mean guessing which ones can still churn.
    // This records the word that has actually been autoplayed, so however often the effect
    // re-runs, a given word autoplays exactly once. Manual taps on the speaker are unaffected
    // — they call speak() directly and are supposed to repeat.
    // The WORD is in the key, not just its position. A skip removes the word at `idx` and
    // shifts the rest left, so a different word lands on the same index — and keying by index
    // alone made the guard say "already played" and leave the new word silent. Alan: "when you
    // skip the word, the next question isn't auto-read." Mine, from the LB-278 narration fix,
    // where keying by index was the thing that stopped Android double-speaking.
    const autoplayKey = `${sessionKey}:${round}:${idx}:${word ? word.word : ""}`;
    if (autoplayedRef.current === autoplayKey) return;
    const t = setTimeout(() => { autoplayedRef.current = autoplayKey; speak(true); }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, sessionKey, round, phase, speechOk, voiceReady, audioReady]);

  useEffect(() => () => { if (advanceRef.current) clearTimeout(advanceRef.current); stopSpeaking(); reportStopped(); }, []);

  // Only bail to the "no words" placeholder before a test has started — once "testing" or
  // "finished", the session's words are genuinely locked in (see the session state above),
  // and the live `words` prop can legitimately go empty right as a full-marks pass finishes
  // (the words that were just tested get auto-unticked on payout), which must not blow away
  // the results screen and its celebration mid-render.
  if ((!words || words.length === 0) && phase === "intro") return (
    <div className="bg-white rounded-2xl p-6 text-center text-base text-slate-400 shadow-sm border border-slate-100">
      {hasAnyWords ? "No words selected for the next test yet — ask a parent to tick some words in Learn & Earn." : "No spelling words set up yet — ask a parent to add some."}
    </div>
  );

  function startTest() {
    // The one guaranteed user gesture before the first word autoplays a second later. iOS
    // only lets an <audio> element play programmatically once it has played inside a real
    // tap, so this is what makes tier-1 clips autoplay at all (TTS_SCOPE.md §8.5). No-op
    // when there are no clips, which is every session until T2/T3 land.
    unlockAudio();
    const drawn = shuffle(words || []);
    setSession(drawn);
    setSlots(drawn.map(() => "pending"));
    slotIdxRef.current = 0;
    deferredRef.current = new Set(); // nothing parked at the start of a test
    firstTryRef.current = {}; firstAnswersRef.current = []; answersRef.current = []; stoppedReportedRef.current = false; setRound(0); setRetryWords(null); setTotalWords(drawn.length); setSessionKey((k) => k + 1); setIdx(0); setInput(""); setResult(null); setCorrectCount(0); setAnswers([]); answersRef.current = []; setAwarded(false); setCapNotice(null); opCorrectRef.current = 0; setPhase("testing");
  }
  function restart() { stopSpeaking(); reportStopped(); setPhase("intro"); }

  // Everything about HOW a word is spoken now lives in lib/speech.js — tier 1 (a
  // pre-generated clip when the word carries one), tier 2 (the device voice), tier 3
  // (nothing worked). This is just the wiring: which text, how fast, and what to do when
  // even tier 2 gives up.
  //
  // rate 0.85 matches what the spelling prompt has always used, and is now applied at
  // PLAYBACK rather than baked into a clip (TTS_SCOPE.md §7.2) — so the same clip serves
  // narration at full speed, and re-tuning this number never regenerates anything.
  //
  // audioKey is null until the hash resolves, and stays null for any word that has never
  // been generated — a parent's own custom word, or anything added since the last backfill
  // (§5.3). Both fall straight through to the device voice, so a missing clip costs one
  // 404 and nothing else.
  function speak(isAutoplay) {
    if (!word) return;
    if (!speechOk) { setSpeechOk(true); resetRetryBudget(); } // manual retry tap
    // Speaks speechTextForWord (speakAs when set, else the word itself) — not word.word
    // directly. This has to match what was hashed above, or tier 1 and tier 2 disagree
    // about what's being said; it also matters on its own for tier 2 specifically, since
    // a homophone's whole point is the disambiguating sentence ("Hour. In one hour.
    // Hour.") — falling back to the bare word alone would silently drop that back to
    // ambiguous, defeating why speakAs exists in the first place.
    speakText(speechTextForWord || word.word, {
      audioKey,
      rate: 0.85,
      isAutoplay,
      onUnavailable: () => setSpeechOk(false),
    });
  }
  // 12 Sep: a blank answer can no longer be submitted (Check is disabled until something is
  // typed) and Skip is the deliberate way past a word the child cannot do — it scores as a
  // wrong answer, so the test still measures what they know, and it keeps them moving rather
  // than stuck on one word.
  function check(skipped = false) {
    if (result !== null) return;
    if (!skipped && !input.trim()) return;
    // First skip parks the word at the end of the round and scores nothing; the child sees
    // it again before the round is out. Second skip (or a skip on the last word, which has
    // nothing to come back after) is final and scores wrong.
    if (skipped && !deferredRef.current.has(word.word)) {
      const reordered = deferToEnd(session, idx);
      if (reordered) {
        deferredRef.current.add(word.word);
        setSession(reordered);
        setInput("");
        setResult(null);
        // Alan, 15 Sep: a circled hyphen holds the word's own position so the child can see it
        // was kept rather than lost, and an orange dot joins the end where it comes back. The
        // word itself is never shown — this is a spelling test, and naming the word would hand
        // over the answer. That was the first version of this and it was wrong.
        // The index is CAPTURED here, not read inside the updater. React runs a functional
        // updater during its own render pass, by which time the increment below has already
        // executed — so reading the ref in there marks the NEXT slot and leaves slot 0 for
        // ever pending. Alan caught it in a screenshot: the first dot stayed grey on a word
        // he had got right. Same shape as the `answers` staleness in this file: mutable state
        // read inside a deferred closure.
        const at = slotIdxRef.current;
        setSlots((prev) => {
          const next = [...prev];
          next[at] = "skipped";
          next.push("requeued");
          return next;
        });
        slotIdxRef.current = at + 1;
        return;
      }
    }
    const correct = !skipped && input.trim().toLowerCase() === word.word.toLowerCase();
    // Answered slots show right (teal) or wrong (mid-grey, not red — Alan, 16 Sep: "red is
    // brutal"). Grey says "that one's done and it wasn't right" without a row of red building up.
    const slotAt = slotIdxRef.current; // captured — see the note in the skip branch above
    setSlots((prev) => { const next = [...prev]; next[slotAt] = correct ? "correct" : "wrong"; return next; });
    slotIdxRef.current = slotAt + 1;
    setResult(correct ? "correct" : "wrong");
    if (correct) {
      playDing(); setCorrectCount((c) => c + 1);
      if (round === 0) opCorrectRef.current += 1;
      // Practice pays its Oink the moment the word is right, so quitting mid-round keeps
      // what was earned — the session's own Oinks were already paid when the test settled.
      else if (oinksOn && onEarnOinks) onEarnOinks(1);
    } else { playError(); }
    const entry = { word: word.word, given: skipped ? "" : input.trim(), correct, skipped };
    // Mirrored into a ref because next() can run from a 700ms auto-advance timer, whose
    // closure was captured BEFORE this answer was committed to state. Reading `answers` there
    // is reliably one behind. It never showed while only the retry set was derived from it —
    // a final wrong answer goes through Continue, which has a fresh closure — but it made the
    // first-attempt score short by one the moment the score started reading the same array.
    answersRef.current = [...answersRef.current, entry];
    setAnswers((a) => [...a, entry]);
    if (!(word.word in firstTryRef.current)) firstTryRef.current[word.word] = correct;
    // Correct answers stay snappy — a 700ms beat and straight on to the next word. A wrong
    // answer used to auto-advance after 3 seconds; it now waits for the child instead
    // (W1.11, Aug 2026): the reveal ("❌ Not quite — it was 'word'") stays on screen with an
    // explicit choice — Continue to the next word, or Restart to reload the whole test from
    // word 1. No timer races the child's reading, and Restart can't vanish mid-decision.
    if (correct) advanceRef.current = setTimeout(() => { next(); }, 700);
  }
  function next() {
    if (idx + 1 < session.length) { setIdx((i) => i + 1); setInput(""); setResult(null); return; }
    // Reached the end of this round.
    if (round === 0) {
      // The first attempt IS the test (Alan, 22 Sep): reaching the last word settles it.
      // The results screen comes straight up with the reward already paid — see the finish
      // effect — and the practice round is offered from there rather than entered
      // automatically. A child who walks away at this point loses nothing, because nothing
      // is left unsaved to lose. Before this, a test with any miss rolled into the practice
      // round unsettled, and leaving that round dropped the whole test: no payout, no
      // activity row, no Coach entry (Freddie's test, 22 Sep).
      firstAnswersRef.current = answersRef.current;
      setPhase("finished");
      return;
    }
    // End of the (optional) practice round.
    setPhase("practicedone");
  }
  // Entered from the results screen only. The retry set is looked up in `session`, not the
  // live `words` prop: a word that has since been unticked or rotated out of `words` was
  // still legitimately part of this attempt and is still worth practising.
  function startPractice() {
    const wrongWords = firstAnswersRef.current.filter((a) => !a.correct).map((a) => session.find((w) => w.word === a.word)).filter(Boolean);
    if (!wrongWords.length) return;
    setRound(1);
    setRetryWords(wrongWords);
    setSession(shuffle(wrongWords));
    setSessionKey((k) => k + 1);
    setIdx(0); setInput(""); setResult(null); setCorrectCount(0); setAnswers([]); answersRef.current = [];
    setPhase("testing");
  }

  if (phase === "intro") {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
        <div className="mb-2 flex justify-center"><Icon id="ic-spelling" size={36} hue="indigo" /></div>
        <div className="font-semibold text-slate-700 text-sm mb-1">{words.length} word{words.length === 1 ? "" : "s"} this test</div>
        <p className="text-base text-slate-500 mb-4">
          {weeklyCapMessage
            ? weeklyCapMessage
            : onCooldown
            ? `Practice only. You've already earned ${perWeek > 1 ? `all ${perWeek} rewards` : "this week's reward"} for Spelling, resets Monday.`
            : doubleDay
            ? (passReward > 0
                ? <>Get every word right to earn <DoubledTokens plus={false} base={reward} /> tokens. {passThreshold}% or more earns <DoubledTokens plus={false} base={passReward} />. <span className="text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 whitespace-nowrap">{ct("tests.double_token_chip", "Double token day")}</span></>
                : <>Get every word right to earn <DoubledTokens plus={false} base={reward} /> tokens. <span className="text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 whitespace-nowrap">{ct("tests.double_token_chip", "Double token day")}</span></>)
            : passReward > 0 ? `Get every word right to earn ${reward} tokens. ${passThreshold}% or more earns ${passReward}.` : `Get every word right to earn ${reward} tokens.`}
        </p>
        {oinksOn && <OinkIntroLine moneyPaused={!!weeklyCapMessage || onCooldown} />}
        <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm w-full`} onClick={startTest}>{ct("tests.start_button", "Start test")}</button>
      </div>
    );
  }

  return (
    <TestOverlay onClose={restart} focused={phase === "testing"}>
      {phase === "finished" ? (() => {
        const full = firstTryCorrect === totalWords && totalWords > 0;
        const passed = !full && passReward > 0 && firstTryPct >= passThreshold;
        const rewardEarned = (full || passed) && !onCooldownAtFinishRef.current && !capNotice;
        const displayCorrect = firstTryCorrect;
        const pct = Math.round(firstTryPct);
        const oink = oinkSessionOP(opCorrectRef.current, full);
        return (
          <div className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
            {rewardEarned && <Celebration variant="pig" emojiOverride={partyEmoji} />}
            <div className={`font-extrabold text-3xl mb-1 ${full ? "text-emerald-500" : "text-slate-700"}`}>{scoreEmoji(pct)} {displayCorrect}/{totalWords}</div>
            {capNotice ? (
              <div className="text-amber-600 font-bold text-base mb-4">{capNotice}</div>
            ) : rewardEarned ? (
              <TokenRewardRow
                amount={full ? reward : passReward}
                detail={full ? `${totalWords}/${totalWords} first time. Full marks.` : `${firstTryCorrect}/${totalWords} first time, ${pct}%. Pass mark ${passThreshold}%.`}
              />
            ) : full || passed ? (
              <div className="text-slate-500 text-base mb-4">{full ? "Full marks." : `${pct}% first time.`} You've already earned {perWeek > 1 ? "all this week's Spelling rewards" : "this week's Spelling reward"}, resets Monday.</div>
            ) : (
              // "So close" only when it is: one more right answer would have paid (the pass
              // tier, or full marks when there is no pass tier). A 0/15 read "So close." (Alan,
              // 23 Sep) — below that line it names what pays and encourages instead.
              (() => {
                const need = passReward > 0 ? Math.ceil((passThreshold * totalWords) / 100) : totalWords;
                const close = firstTryCorrect + 1 >= need;
                return (
                  <div className="text-slate-500 text-base mb-4" data-testid="spelling-miss-line">{passReward > 0
                    ? (close
                      ? ct("tests.spelling.close_pass", "Get every word right to earn {f} tokens, or {t}% for {p}. So close.", { f: reward, t: passThreshold, p: passReward })
                      : ct("tests.spelling.low_pass", "Get every word right to earn {f} tokens, or {t}% for {p}. Keep practising and it will come.", { f: reward, t: passThreshold, p: passReward }))
                    : (close
                      ? ct("tests.spelling.close_full", "Get every word right to earn {f} tokens. So close.", { f: reward })
                      : ct("tests.spelling.low_full", "Get every word right to earn {f} tokens. Keep practising and it will come.", { f: reward }))}</div>
                );
              })()
            )}
            {oinksOn && <OinkRewardRow op={oink.op} detail={oink.detail} />}
            {/* The words missed FIRST time, which is what the score above counts and what the
                child actually needs to learn — not what survived the practice round, which
                by design is usually nothing. */}
            {!full && firstWrong.length > 0 && (
              <div className="text-left flex flex-col gap-1.5 mb-4">
                {firstWrong.map((a, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2 text-xs font-semibold bg-rose-50 text-rose-700">
                    <span className="flex items-center gap-1.5"><Icon id="ic-close" size={14} hue="rose" />{a.word}</span>
                    <span className="text-rose-400 truncate ml-2">{a.given ? `you wrote "${a.given}"` : "no answer"}</span>
                  </div>
                ))}
              </div>
            )}
            {firstWrong.length > 0 && (
              <button className="w-full bg-indigo-100 text-indigo-900 font-bold rounded-xl px-5 py-2.5 text-sm mb-2" data-testid="practice-offer" onClick={startPractice}>{ct("tests.practice_offer", "Practise the {n} I missed", { n: firstWrong.length })}</button>
            )}
            <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm w-full`} onClick={restart}>{ct("tests.back_to_le", "Back to Learn & Earn")}</button>
          </div>
        );
      })() : phase === "practicedone" ? (() => {
        // K3 (signed off 22 Sep): closes the practice round. Display only — the practice
        // Oinks were paid word by word in check(), and the test's tokens at settlement.
        const practiced = retryWords ? retryWords.length : 0;
        const full = firstTryCorrect === totalWords && totalWords > 0;
        const passed = !full && passReward > 0 && firstTryPct >= passThreshold;
        const earnedAmt = full ? reward : passed ? passReward : 0;
        const rewardEarned = earnedAmt > 0 && !onCooldownAtFinishRef.current && !capNotice;
        return (
          <div className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center" data-testid="practice-done">
            <div className="font-extrabold text-xl text-slate-800 mb-1">{ct("tests.practicedone_title", "Practice done")}</div>
            <div className="text-slate-500 text-base mb-4">{ct("tests.practicedone_sub", "{c} of {n} right this time", { c: correctCount, n: practiced })}</div>
            {oinksOn && correctCount > 0 && <OinkRewardRow op={correctCount} detail={ct("tests.practicedone_oinks", "For practising")} />}
            <div className="text-xs text-slate-400 font-semibold mb-4" data-testid="practice-done-tokens">
              {rewardEarned
                ? ct("tests.practicedone_tokens", "{n} tokens earned from your test, {c}/{t}", { n: earnedAmt, c: firstTryCorrect, t: totalWords })
                : ct("tests.practicedone_score", "Your test: {c} of {t} first time", { c: firstTryCorrect, t: totalWords })}
            </div>
            <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm w-full`} onClick={restart}>{ct("tests.back_to_le", "Back to Learn & Earn")}</button>
          </div>
        );
      })() : (
        <div key={`q-${sessionKey}-${round}-${idx}`} className="bg-white rounded-2xl px-3 py-5 shadow-sm border border-slate-100 text-center">
          <div className="flex flex-col mb-3">
            {/* Alan, 13 Sep: the practice round has to SAY it is practice. It no longer moves
                the score either way, and a child retyping a word they just got wrong should
                know that is what it is for, not think they are clawing the reward back. */}
            {/* Layout C (Alan, 15 Sep). The banner, dots, position line and Quit were all
                children of ONE flex row, so the banner sat inline and squeezed the position
                text instead of stacking. Now the banner takes exactly the slot the dots take
                in a normal round — same header height in both states, nothing jumps when the
                practice round starts — and the position line stops saying "practice" a third
                time. Two rows: [banner | dots] then [position · Quit]. */}
            {round === 1 ? (
              <div className="rounded-xl bg-indigo-50 border border-indigo-200 px-3 py-2 mb-2 flex items-center gap-2" data-testid="practice-banner">
                <Icon id="ic-reassure" size={18} hue="indigo" />
                <span className="text-sm font-bold text-indigo-900">{ct("spelling.practice_banner", "Practice. Your tokens are already saved.")}</span>
              </div>
            ) : <ProgressDots slots={slots} />}
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-slate-400 font-medium text-left">{round === 1
                ? `Word ${idx + 1} of ${session.length}`
                : `Word ${idx + 1} of ${session.length}${comingBack ? ` · ${comingBack} coming back` : ""} · ${onCooldown ? "practice only, reward resets Monday" : passReward > 0 ? `${reward} tokens for full marks, ${passReward} for ${passThreshold}%` : `full marks earns ${reward} tokens`}`}</div>
              <button className="lb-inline-quit text-xs text-slate-400 font-semibold flex-shrink-0 ml-2" onClick={restart}>{ct("tests.quit_restart", "Quit / Restart")}</button>
            </div>
          </div>
          {/* LB-292 (Alan: "speak and show the 'as in'"). Shown as well as spoken, because a
              child who mishears the spoken hint has no way to ask for it again in words, and
              because a deaf or hard-of-hearing child gets nothing from the audio at all.
              Showing it is safe: no description contains its own word — that rule was enforced
              when the 189 were authored and checked twice, so the text cannot hand over the
              spelling. It sits ABOVE Listen so it is read before the word is replayed. */}
          {wordDescription && result === null && (
            <p className="text-center text-sm text-slate-500 mb-3 px-4" data-testid="word-description">{wordDescription}</p>
          )}
          {speechOk ? (
            <button onClick={() => speak(false)} className="mx-auto flex items-center gap-2 bg-amber-50 text-amber-700 font-bold rounded-lg px-5 py-3 mb-4">{ct("tests.listen", "Listen")}</button>
          ) : (
            <button onClick={() => speak(false)} className="w-full text-left text-amber-600 bg-amber-50 rounded-xl px-4 py-3 mb-4 text-base font-semibold">{ct("spelling.audio_unavailable", "Audio isn't available right now — tap to try again, or ask a parent to read the word aloud.")}</button>
          )}
          <LetterKeyboard value={input} disabled={result !== null} onEnter={() => check()} maxLength={20} onChange={setInput} />
          {result === null ? (
            /* mt-6, not mt-3 (W1.11): the extra gap between the keyboard's bottom row and
               this button is deliberate — it was close enough to catch accidental pushes. */
            <>
              <button disabled={!input.trim()} className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-base w-full mt-6 disabled:opacity-40`} onClick={() => check()}>{ct("spelling.check_button", "Check spelling")}</button>
              <button className="w-full mt-2 text-sm font-bold text-slate-500 py-2" data-testid="test-skip" onClick={() => check(true)}>{(word && deferredRef.current.has(word.word)) || idx >= session.length - 1 ? ct("tests.skip", "Skip this word") : ct("tests.skip_for_now", "Skip for now")}</button>
            </>
          ) : (
            <div className={`font-bold text-base mt-3 flex items-center justify-center gap-1.5 ${result === "correct" ? "text-emerald-500" : "text-rose-500"}`}>
              <Icon id={result === "correct" ? "ic-celebrate" : "ic-close"} size={18} hue={result === "correct" ? "pink" : "rose"} />
              {result === "correct" ? "Correct" : `Not quite — it was "${word.word}"`}
            </div>
          )}
          {result === "wrong" && (
            <div className="flex gap-2 mt-3">
              <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-4 py-2.5 text-sm flex-1`} onClick={() => next()}>{ct("spelling.continue_button", "Continue")}</button>
              <button className="bg-slate-100 text-slate-600 font-bold rounded-xl px-4 py-2.5 text-sm" onClick={() => { stopSpeaking(); startTest(); }}>{ct("spelling.restart_button", "Restart test")}</button>
            </div>
          )}
        </div>
      )}
    </TestOverlay>
  );
}
